import { takeLatest, call, select, put } from 'redux-saga/effects'
import { CREATE_BOOKABLE, GET_BOOKABLES, UPDATE_BOOKABLE } from './actionTypes'
import { push } from 'connected-react-router'
import { BOOKABLE_ACTIVITIES } from '../../routesConstants'
import { setLoader } from '../loader/actions'
import { makeSelectUser } from '../auth/selectors'
import { setBookables } from './actions'
import bookableService from '../../supabaseServices/BookableService'
import locationService from '../../supabaseServices/LocationService'
import { setSnackbar } from '../snackbar/actions'
import { defaultFormatDate } from '../../utils/format'

export function* createBookableSagaFn(payload) {
  try {
    yield put(setLoader(true))
    const user = yield select(makeSelectUser())
    const timezone = yield call(locationService.getTimezone, payload.data.location)
    const locationUUID = yield call(locationService.createLocation, {
      ...payload.data.location,
      created_by: user.uuid,
      timezone: timezone.timeZoneId,
    })

    const bookableData = {
      type: payload.data.type,
      name: payload.data.name,
      cancellation_policy_uuid: payload.data.cancellation_policy_uuid,
      created_by: user.uuid,
      currency: payload.data.currency.toUpperCase(),
      location_uuid: locationUUID,
      long_description: payload.data.long_description,
      max_capacity: parseInt(payload.data.max_capacity),
      min_travelers_per_booking: parseInt(payload.data.min_travelers_per_booking),
      max_travelers_per_booking: parseInt(payload.data.max_travelers_per_booking),
      price: payload.data.price,
      short_description: payload.data.short_description,
      supplier_uuid: payload.data.supplier_uuid,
      bookable_provider: payload.data.bookable_provider,
      photo_urls: payload.data.photo_urls,
      guest_requirements: payload.data.guest_requirements,
      status: payload.data.status,
    }

    const activityData = {
      duration: payload.data.duration,
      category: payload.data.category,
    }

    // const bookableUUID = createBookableData[0]?.uuid

    if (payload.data.ageBands.length > 0 && payload.data.schedules.length > 0) {
      const updatedAgeBands = payload.data.ageBands.map((band) => ({
        ...band,
        age_band_type: band.age_band_type,
      }))

      const updatedSchedules = payload.data.schedules.map((schedule) => ({
        ...schedule,
        created_by: user.uuid,
        start_date: defaultFormatDate(new Date(schedule.start_date)),
        earliest_start_time: defaultFormatDate(new Date(schedule.earliest_start_time)),
        latest_start_time: defaultFormatDate(new Date(schedule.latest_start_time)),
      }))

      // Calls the new transaction to create everything atomically. Rolls it all back if it fails.
      yield call(
        bookableService.createBookableActivityTransaction,
        bookableData,
        activityData,
        updatedSchedules,
        updatedAgeBands,
      )
    }

    const res = yield call(bookableService.getPaginatedBookables, 1)
    yield put(setBookables(res))
    yield put(push(BOOKABLE_ACTIVITIES))
  } catch (error) {
    console.log('err', { error })
    yield put(
      setSnackbar({
        open: true,
        severity: 'error',
        message: `${error.message ? error.message : 'Something went wrong, contact administrator'}`,
      }),
    )
  } finally {
    yield put(setLoader(false))
  }
}

export function* getBookablesSagaFn() {
  try {
    yield put(setLoader(true))
    const res = yield call(bookableService.getPaginatedBookables, 1)
    yield put(setBookables(res))
  } catch (error) {
    console.log(error)
  } finally {
    yield put(setLoader(false))
  }
}

export function* updateBookableSagaFn({ data }) {
  try {
    yield put(setLoader(true))
    const user = yield select(makeSelectUser())

    if (data) {
      yield call(bookableService.updateBookable, data.uuid, {
        name: data.name,
        type: data.type,
        price: data.price,
        max_capacity: data.max_capacity,
        min_travelers_per_booking: data.min_travelers_per_booking,
        max_travelers_per_booking: data.max_travelers_per_booking,
        cancellation_policy_uuid: data.cancellation_policy_uuid,
        currency: data.currency,
        supplier_uuid: data.supplier_uuid,
        location_uuid: data.location_uuid,
        short_description: data.short_description,
        long_description: data.long_description,
        updated_by: user.uuid,
        bookable_provider: data.bookable_provider,
        photo_urls: data.photo_urls,
        guest_requirements: data.guest_requirements,
        status: data.status,
      })
    }

    if (data.ageBands?.length) {
      yield call(bookableService.deleteAgeBandsByBookableUUID, data.uuid)

      for (const band of data.ageBands) {
        yield call(bookableService.createAgeBands, { ...band, bookable_uuid: data.uuid })
      }
    }

    const existingSchedules = yield call(bookableService.getSchedulesByBookableUUID, data.uuid)

    const existingScheduleIds = existingSchedules.data?.map((schedule) => schedule.id)
    if (data.schedules?.length) {
      const incomingScheduleIds = data.schedules.map((schedule) => schedule.id).filter(Boolean)
      for (const schedule of data.schedules) {
        const formattedSchedule = {
          ...schedule,
          updated_by: user.uuid,
          bookable_uuid: data.uuid,
          start_date: defaultFormatDate(new Date(schedule.start_date)),
          earliest_start_time: defaultFormatDate(new Date(schedule.earliest_start_time)),
          latest_start_time: defaultFormatDate(new Date(schedule.latest_start_time)),
          deleted_at: schedule.deleted_at ? defaultFormatDate(new Date(schedule.deleted_at)) : null,
          created_at: schedule.created_at
            ? defaultFormatDate(new Date(schedule.created_at))
            : defaultFormatDate(new Date()),
          updated_at: schedule.updated_at ? defaultFormatDate(new Date(schedule.updated_at)) : null,
          ...(schedule.id ? {} : { created_by: user.uuid }),
        }
        if (schedule.id) {
          yield call(bookableService.updateBookableSchedule, schedule.id, formattedSchedule)
        } else {
          yield call(bookableService.createBookableSchedules, [formattedSchedule])
        }
      }

      for (const existingId of existingScheduleIds) {
        if (!incomingScheduleIds.includes(existingId)) {
          yield call(bookableService.updateBookableSchedule, existingId, {
            deleted: true,
            deleted_at: defaultFormatDate(new Date()),
            deleted_by: user.uuid,
          })
        }
      }
    } else {
      for (const existingId of existingScheduleIds) {
        yield call(bookableService.updateBookableSchedule, existingId, {
          deleted: true,
          deleted_at: defaultFormatDate(new Date()),
          deleted_by: user.uuid,
        })
      }
    }

    const res = yield call(bookableService.getPaginatedBookables, 1)
    yield put(setBookables(res))
    yield put(push(BOOKABLE_ACTIVITIES))
  } catch (error) {
    console.error('Error updating bookable:', error)
    yield put(
      setSnackbar({
        open: true,
        severity: 'error',
        message: error.message || 'Failed to update bookable.',
      }),
    )
  } finally {
    yield put(setLoader(false))
  }
}

export default function* supplierSaga() {
  yield takeLatest(CREATE_BOOKABLE, createBookableSagaFn)
  yield takeLatest(GET_BOOKABLES, getBookablesSagaFn)
  yield takeLatest(UPDATE_BOOKABLE, updateBookableSagaFn)
}
